import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Package, ArrowLeft, Download } from 'lucide-react';
import { useSetCards, useImportSetCards } from '@/hooks/useApi';
import toast from 'react-hot-toast';

function CardImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

export default function SetDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const game = searchParams.get('game');
  const setName = searchParams.get('set_name');
  const [importing, setImporting] = useState(false);

  const { data: cards = [], isLoading, refetch } = useSetCards(game, setName);
  const { mutateAsync: importSet } = useImportSetCards();

  const handleImport = async () => {
    if (!game || !setName) return;
    setImporting(true);
    try {
      const result = await importSet({ game, set_name: setName });
      toast.success(`Imported ${result.imported} card${result.imported !== 1 ? 's' : ''} from ${setName}`);
      refetch();
    } catch {
      toast.error('Failed to import set cards');
    } finally {
      setImporting(false);
    }
  };

  const owned = (cards as any[]).filter((c) => c.owned).length;
  const total = (cards as any[]).length;
  const pct = total > 0 ? Math.round((owned / total) * 100) : 0;

  if (!game || !setName) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500 dark:text-zinc-500">Missing set information.</p>
        <Link to="/collection" className="text-brand-600 dark:text-brand-400 hover:underline text-sm mt-2 inline-block">
          Back to collection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-zinc-500 mb-1">{game}</p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{setName}</h1>
          </div>
          <button
            onClick={handleImport}
            disabled={importing}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl border border-brand-200 text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/20 hover:bg-brand-100 dark:bg-brand-900/40 text-sm font-medium disabled:opacity-50 transition-colors"
            title="Fetch all cards in this set from the card database"
          >
            <Download className={`h-4 w-4 ${importing ? 'animate-bounce' : ''}`} />
            {importing ? 'Importing…' : 'Import set cards'}
          </button>
        </div>

        {/* Completion bar */}
        {!isLoading && total > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-500 dark:text-zinc-500 mb-1.5">
              <span>{owned} / {total} cards owned</span>
              <span className="font-semibold text-brand-600 dark:text-brand-400">{pct}%</span>
            </div>
            <div className="h-2.5 bg-gray-100 dark:bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-500 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Card grid */}
      {isLoading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {[...Array(24)].map((_, i) => (
            <div key={i} className="aspect-[3/4] rounded-lg bg-gray-100 dark:bg-zinc-800 animate-pulse" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div className="text-center py-24">
          <Package className="h-10 w-10 text-gray-400 dark:text-zinc-500 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-zinc-500 font-medium">No cards in database for this set yet.</p>
          <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1 mb-6">Click "Import set cards" above to fetch all cards from the {game} card database.</p>
          <button
            onClick={handleImport}
            disabled={importing}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            <Download className={`h-4 w-4 ${importing ? 'animate-bounce' : ''}`} />
            {importing ? 'Importing…' : `Import ${setName} cards`}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {(cards as any[]).map((card: any) => {
            const userImage = (card.collection_image_urls as string[] | null)?.[0] ?? null;

            if (card.owned) {
              return (
                <div
                  key={card.id}
                  className="flex flex-col items-center text-center"
                  title={card.name}
                >
                  <div className="w-full aspect-[3/4] rounded-lg overflow-hidden mb-1 ring-2 ring-brand-400">
                    <CardImage
                      src={userImage}
                      alt={card.name}
                      cardName={card.name}
                      cardNumber={card.card_number}
                      game={card.game}
                      className="w-full h-full"
                    />
                  </div>
                  <p className="text-xs text-gray-700 dark:text-zinc-300 leading-tight line-clamp-2 font-medium">{card.name}</p>
                  {card.card_number && (
                    <p className="text-xs text-gray-500 dark:text-zinc-500">#{card.card_number}</p>
                  )}
                  {card.condition && (
                    <p className="text-xs text-brand-500">{card.condition}</p>
                  )}
                </div>
              );
            }

            // Missing — dimmed placeholder, click → explore
            return (
              <button
                key={card.id}
                onClick={() => navigate(`/explore?q=${encodeURIComponent(card.name)}`)}
                className="group flex flex-col items-center text-center hover:opacity-80 transition-opacity"
                title={`Find ${card.name} on the marketplace`}
              >
                <div className="w-full aspect-[3/4] rounded-lg overflow-hidden mb-1 opacity-40 group-hover:ring-2 ring-brand-400 transition-all">
                  <CardImage
                    src={undefined}
                    alt={card.name}
                    cardName={card.name}
                    cardNumber={card.card_number}
                    game={card.game}
                    className="w-full h-full"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-zinc-500 leading-tight line-clamp-2">{card.name}</p>
                {card.card_number && (
                  <p className="text-xs text-gray-500 dark:text-zinc-500">#{card.card_number}</p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      {!isLoading && total > 0 && (
        <div className="flex items-center gap-6 mt-8 pt-6 border-t border-gray-200 dark:border-zinc-800 text-xs text-gray-500 dark:text-zinc-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-100 dark:bg-brand-900/40 ring-2 ring-brand-400" />
            <span>Owned</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-200 dark:bg-zinc-700 opacity-60" />
            <span>Missing — click to explore marketplace</span>
          </div>
        </div>
      )}
    </div>
  );
}
